import type { TFunction } from 'i18next'
import type { SaasApiError } from '../../../api'

const PROVIDER_BALANCE_MARKERS = ['insufficient balance', 'insufficient_balance', 'quota exceeded']

function looksLikeProviderBalance(message: string): boolean {
  const lowered = message.toLowerCase()
  return PROVIDER_BALANCE_MARKERS.some((m) => lowered.includes(m))
}

function looksLikeVariationPrefixedProviderError(message: string): boolean {
  return /[\u4e00-\u9fff]{2,8}:\s/.test(message) && looksLikeProviderBalance(message)
}

/**
 * VistoeraTASK-00179 granular, provider-neutral error taxonomy — these
 * codes are set ONLY by our own backend
 * (`voice_tenant_error_messages.classify_provider_execution_error` and the
 * reference-audio validators in `voice_reference_provider_delivery.py`),
 * always paired with a pre-written, tenant-safe Chinese `message`. A
 * provider 4xx/429/5xx must never flatten into the generic
 * "generateFailed"/"generationFailed" copy — surface the specific,
 * already-safe message instead.
 */
const SAFE_PROVIDER_TAXONOMY_CODES = new Set([
  'VOICE_REFERENCE_INVALID',
  'VOICE_REFERENCE_TOO_SHORT',
  'VOICE_REFERENCE_FORMAT_UNSUPPORTED',
  'VOICE_PROVIDER_CREDENTIAL_ERROR',
  'VOICE_PROVIDER_UPLOAD_FAILED',
  'VOICE_PROVIDER_REQUEST_REJECTED',
  'VOICE_ID_CONFLICT',
  'VOICE_PROVIDER_RATE_LIMIT',
  'VOICE_PROVIDER_QUOTA',
  'VOICE_PROVIDER_TEMPORARY_ERROR',
  'VOICE_RESPONSE_INVALID',
  'VOICE_PERSISTENCE_FAILED',
  'VOICE_INTERNAL_ERROR',
  // VistoeraTASK-00180 — additional granular MiniMax voice-clone taxonomy
  // codes (previously all collapsed into a generic UNKNOWN_ERROR/
  // VALIDATION_ERROR bucket).
  'VOICE_REFERENCE_DURATION_INVALID',
  'PROVIDER_PERMISSION_DENIED',
  'VOICE_REFERENCE_QUALITY_INVALID',
  'VOICE_CLONE_REQUEST_INVALID',
])

/**
 * Canonical tenant-facing error for Canvas Voice Template generation.
 * Never surfaces raw provider strings (e.g. "insufficient balance") or
 * internal variation labels (e.g. "沉稳专业: …").
 */
export function formatCanvasVoiceTemplateError(
  err: Pick<SaasApiError, 'code' | 'message' | 'status'> | null | undefined,
  t: TFunction<'canvas'>,
): string {
  if (!err) return t('voiceTemplate.errors.generateFailed')

  const code = err.code || ''
  const message = (err.message || '').trim()

  if (code === 'PROVIDER_BALANCE_INSUFFICIENT' || code === 'INSUFFICIENT_PROVIDER_BALANCE') {
    return t('voiceTemplate.errors.providerBalanceInsufficient')
  }

  if (SAFE_PROVIDER_TAXONOMY_CODES.has(code)) {
    return message || t('voiceTemplate.errors.generationFailed')
  }

  if (code === 'voice_capability_not_ready' || err.status === 503) {
    if (
      !message ||
      looksLikeProviderBalance(message) ||
      looksLikeVariationPrefixedProviderError(message)
    ) {
      return t('voiceTemplate.errors.serviceUnavailable')
    }
    // Backend should already sanitize; fall through only for known-safe Chinese copy.
    if (/[\u4e00-\u9fff]/.test(message) && !looksLikeProviderBalance(message)) {
      return message
    }
    return t('voiceTemplate.errors.serviceUnavailable')
  }

  if (code === 'member_credit_limit_exceeded') {
    return t('generation.errors.memberCreditLimitExceeded')
  }
  if (code === 'insufficient_credits' || err.status === 402) {
    return t('voiceTemplate.errors.pointsInsufficient')
  }

  if (code === 'no_candidates' || code === 'no_preview_audio' || code === 'VOICE_DESIGN_FAILED') {
    return t('voiceTemplate.errors.generationFailed')
  }

  if (code === 'AUTH_ERROR' || code === 'not_authenticated' || code === 'invalid_session' || err.status === 401) {
    return t('voiceTemplate.errors.sessionExpired')
  }

  if (code === 'RATE_LIMITED') {
    return t('voiceTemplate.errors.rateLimited')
  }

  if (/timeout/i.test(message) || err.status === 504) {
    return t('voiceTemplate.errors.timeout')
  }

  if (code === 'invalid_input' || err.status === 422) {
    return t('voiceTemplate.errors.invalidInput')
  }

  if (looksLikeProviderBalance(message) || looksLikeVariationPrefixedProviderError(message)) {
    return t('voiceTemplate.errors.serviceUnavailable')
  }

  if (message && /[\u4e00-\u9fff]/.test(message)) {
    return message
  }

  if (message && !/^[a-z0-9_ .:-]+$/i.test(message)) {
    return message
  }

  return t('voiceTemplate.errors.generateFailed')
}
